/**
 * Parse blockchain/contract errors into user-friendly messages
 */

// Common error messages mapping
const ERROR_MESSAGES = {
  // Contract errors
  'Already registered': 'You are already registered with AlphaMeta.',
  'Not registered': 'Please register first to continue.',
  'User blacklisted': 'Your account has been restricted. Please contact support.',
  'Invalid referrer': 'The referral ID you entered is not valid.',
  'Below minimum investment': `Minimum investment is $10 USDT.`,
  'Must be multiple of $10': 'Investment amount must be a multiple of $10.',
  'Cannot invest less than last package': 'New investment must be equal to or greater than your last investment.',
  'Investment not active': 'This investment is no longer active.',
  'Trade already active': 'Trading is already active for this investment.',
  'Trade not active': 'Please start trading first before claiming profits.',
  'Timeframe not passed': 'Please wait for the trading period to complete.',
  'No rewards': 'You have no pending rewards to withdraw.',
  'Insufficient contract balance': 'Contract has insufficient funds. Please try again later.',
  'Leader investment cannot trade': 'Leader investments do not participate in trading.',
  
  // Token errors
  'transfer amount exceeds balance': 'Insufficient USDT balance in your wallet.',
  'insufficient allowance': 'Please approve the token transfer first.',
  'ERC20: transfer amount exceeds balance': 'Insufficient USDT balance in your wallet.',
  'ERC20: insufficient allowance': 'Please approve the token transfer first.',
  
  // Wallet errors
  'user rejected': 'Transaction was cancelled by user.',
  'User rejected': 'Transaction was cancelled by user.',
  'user denied': 'Transaction was cancelled by user.',
  'User denied': 'Transaction was cancelled by user.',
  'ACTION_REJECTED': 'Transaction was cancelled by user.',
  
  // Network errors
  'network changed': 'Network changed. Please reconnect your wallet.',
  'disconnected': 'Wallet disconnected. Please reconnect.',
  'Chain mismatch': 'Please switch to BNB Smart Chain network.',
  
  // Gas errors
  'cannot estimate gas': 'Transaction may fail. Please check your balance and try again.',
  'gas required exceeds': 'Transaction requires more gas than available.',
  'intrinsic gas too low': 'Gas limit too low. Please try again.',
  'out of gas': 'Transaction ran out of gas.',
  
  // Generic errors
  'nonce too low': 'Please wait for your previous transaction to complete.',
  'replacement fee too low': 'Please wait for your previous transaction to complete.',
  'timeout': 'Transaction timed out. Please try again.',
  'TIMEOUT': 'Transaction timed out. Please try again.',
};

/**
 * Parse error and return user-friendly message
 * @param {Error|Object|string} error - The error to parse
 * @returns {string} User-friendly error message
 */
export function parseError(error) {
  if (!error) return 'An unknown error occurred. Please try again.';
  
  // Convert to string for easier parsing
  let errorString = '';
  
  if (typeof error === 'string') {
    errorString = error;
  } else if (error.reason) {
    errorString = error.reason;
  } else if (error.message) {
    errorString = error.message;
  } else if (error.data?.message) {
    errorString = error.data.message;
  } else {
    errorString = JSON.stringify(error);
  }
  
  // Check for known error patterns
  for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
    if (errorString.toLowerCase().includes(pattern.toLowerCase())) {
      return message;
    }
  }
  
  // Try to extract revert reason from the error
  const revertMatch = errorString.match(/reverted with reason string '([^']+)'/);
  if (revertMatch) {
    const reason = revertMatch[1];
    // Check if this reason has a friendly message
    for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
      if (reason.toLowerCase().includes(pattern.toLowerCase())) {
        return message;
      }
    }
    return reason;
  }
  
  // Check for execution reverted
  if (errorString.includes('execution reverted')) {
    // Try to find the specific reason
    const reasonMatch = errorString.match(/reason="([^"]+)"/);
    if (reasonMatch) {
      const reason = reasonMatch[1];
      for (const [pattern, message] of Object.entries(ERROR_MESSAGES)) {
        if (reason.toLowerCase().includes(pattern.toLowerCase())) {
          return message;
        }
      }
      return reason;
    }
    return 'Transaction failed. Please check your balance and try again.';
  }
  
  // Check for Internal JSON-RPC error
  if (errorString.includes('Internal JSON-RPC error')) {
    // This usually means a contract revert
    return 'Transaction failed. Please check your balance and inputs.';
  }
  
  // Check for cannot estimate gas
  if (errorString.includes('cannot estimate gas')) {
    return 'Transaction may fail. Please check:\n• Sufficient USDT balance\n• Valid referral ID\n• Correct network (BNB Chain)';
  }
  
  // Generic fallback with truncated message
  if (errorString.length > 100) {
    return 'Transaction failed. Please check your wallet and try again.';
  }
  
  return errorString || 'An unknown error occurred. Please try again.';
}

/**
 * Check if error is a user rejection
 * @param {Error|Object|string} error 
 * @returns {boolean}
 */
export function isUserRejection(error) {
  const errorString = typeof error === 'string' ? error : (error?.message || error?.reason || '');
  const rejectionPatterns = ['user rejected', 'user denied', 'ACTION_REJECTED', 'cancelled'];
  return rejectionPatterns.some(pattern => errorString.toLowerCase().includes(pattern.toLowerCase()));
}

export default parseError;
